// Enums
export enum MountType { Wall = 'wall', Ceiling = 'ceiling' }
export enum HeadingStyle { SFold = 's-fold', PinchPleat = 'pinch-pleat', Eyelet = 'eyelet', PencilPleat = 'pencil-pleat' }
export enum FabricType { Linen = 'linen', Velvet = 'velvet', Cotton = 'cotton', Silk = 'silk', Jacquard = 'jacquard', Polyester = 'polyester' }
export enum Opacity { Sheer = 'sheer', LightFiltering = 'light-filtering', RoomDarkening = 'room-darkening', Blackout = 'blackout' }
export enum LiningType { None = 'none', Standard = 'standard', Thermal = 'thermal', Blackout = 'blackout' }
export enum HardwareStyle { Decorative = 'decorative', Architectural = 'architectural' }
export enum DropStyle { Floating = 'floating', Touching = 'touching', Puddle = 'puddle', SillLength = 'sill-length' }
export enum FabricPattern { Solid = 'solid', Geometric = 'geometric', Floral = 'floral', Striped = 'striped', Herringbone = 'herringbone' }

// Interfaces
export interface WindowConfig {
  roomName: string;
  windowName: string;
  mountType: MountType;
  trackWidth: number;        // mm
  finishedDrop: number;      // mm
  dropStyle: DropStyle;
  floorClearance: number;    // mm (negative for pooling)
}

export interface CurtainStyleConfig {
  headingStyle: HeadingStyle;
  fullnessRatio: number;     // auto-calculated from heading but overridable
  stackBack: number;         // mm, stack-back allowance per side
  returnWidth: number;       // mm, return to wall per side
  overlap: number;           // mm, center overlap (for pair)
  isPair: boolean;           // single panel or pair
}

export interface FabricConfig {
  fabricType: FabricType;
  fabricCode: string;
  fabricWidth: number;       // mm (usable width)
  verticalRepeat: number;    // mm (0 = plain)
  horizontalRepeat: number;  // mm (0 = plain)
  opacity: Opacity;
  pattern: FabricPattern;
  pricePerMeter: number;     // currency per linear meter
}

export interface LiningConfig {
  liningType: LiningType;
  liningWidth: number;       // mm
  pricePerMeter: number;
}

export interface HardwareConfig {
  style: HardwareStyle;
  mountType: MountType;
  trackCode: string;
  pricePerMeter: number;
  bracketCount: number;
  accessoriesPrice: number;
}

// Calculation results
export interface CutCalculation {
  totalFabricWidth: number;          // mm
  numberOfWidths: number;            // integer
  cutDrop: number;                   // mm per width
  patternAdjustment: number;         // mm added for pattern matching
  headingAllowance: number;          // mm
  hemAllowance: number;              // mm
  totalFabricLength: number;         // mm (all widths combined)
  totalFabricMeters: number;         // meters
  wastagePercent: number;
  liningMeters: number;
  panelBreakdown: PanelCut[];
}

export interface PanelCut {
  panelNumber: number;
  cutDrop: number;                   // mm
  finishedWidth: number;             // mm
  patternStartOffset: number;        // mm from selvedge
  seamAllowance: number;             // mm
  isHalfWidth: boolean;
}

export interface PurchaseOrder {
  orderNumber: string;
  date: string;
  customerName: string;
  items: PurchaseOrderItem[];
  totalFabricCost: number;
  totalLiningCost: number;
  totalHardwareCost: number;
  grandTotal: number;
}

export interface PurchaseOrderItem {
  windowRef: string;
  fabricCode: string;
  metersRequired: number;
  liningMeters: number;
  hardwareDescription: string;
}

export interface CutSheet {
  orderNumber: string;
  windowRef: string;
  fabricCode: string;
  fabricWidth: number;
  headingStyle: HeadingStyle;
  mountType: MountType;
  trackWidth: number;
  finishedDrop: number;
  panels: PanelCut[];
  headingPrep: string;
  hemDepth: number;
  notes: string[];
}

export interface OrderSummary {
  windows: WindowOrderSummary[];
  totalPrice: number;
  approvedAt?: string;
  customerSignature?: string;
}

export interface WindowOrderSummary {
  windowConfig: WindowConfig;
  styleConfig: CurtainStyleConfig;
  fabricConfig: FabricConfig;
  liningConfig: LiningConfig;
  hardwareConfig: HardwareConfig;
  calculation: CutCalculation;
  price: number;
}
